import './services.logger';
import { createDeferredPromise } from '../../utils/promise';

// it's a rough number
const TIMEOUT = 3000;

app.service('RecaptchaService', [
  '$timeout',
  'logger',
  'mainConfig',
  function ($timeout, logger, mainConfig) {
    let recaptchaTimer = null;
    let invisibleRecaptchaWidgetId = null;
    let invisibleRecaptchaPromise = null;

    const reset = (elementId) => {
      if (
        typeof grecaptcha !== 'undefined' &&
        document.querySelector('#g-recaptcha-response')
      ) {
        if (document.querySelector('.g-recaptcha#' + elementId) === null) {
          return;
        }
        const widgetId = document.querySelector('.g-recaptcha#' + elementId)
          .dataset.widgetId;
        grecaptcha.reset(widgetId);
      }
    };

    const execute = (elementId) => {
      if (
        typeof grecaptcha !== 'undefined' &&
        document.querySelector('#g-recaptcha-response')
      ) {
        if (document.querySelector('.g-recaptcha#' + elementId) === null) {
          return;
        }
        const widgetId = document.querySelector('.g-recaptcha#' + elementId)
          .dataset.widgetId;
        grecaptcha.execute(widgetId);
      }
    };

    const resetWithWidgetId = (widgetId) => {
      if (
        typeof grecaptcha !== 'undefined' &&
        widgetId !== null &&
        widgetId !== undefined
      ) {
        grecaptcha.reset(widgetId);
      }
    };

    const renderInvisibleRecaptcha = () => {
      if (invisibleRecaptchaWidgetId !== null) {
        return invisibleRecaptchaWidgetId;
      }

      const recaptchaElement = document.createElement('div');
      document.body.appendChild(recaptchaElement);
      const onRecaptchaSuccessed = (token) => {
        document.getElementById('g-recaptcha-response').value = token;
        invisibleRecaptchaPromise?.resolve(token);
        invisibleRecaptchaPromise = null;
        $timeout(() => {
          resetWithWidgetId(invisibleRecaptchaWidgetId);
        }, 50);
      };
      const onRecaptchaRejected = (error) => {
        invisibleRecaptchaPromise?.reject(error);
        invisibleRecaptchaPromise = null;
        $timeout(() => {
          resetWithWidgetId(invisibleRecaptchaWidgetId);
        }, 50);
      };
      invisibleRecaptchaWidgetId = grecaptcha.render(recaptchaElement, {
        sitekey: mainConfig.recaptchaSiteKey,
        size: 'invisible',
        badge: 'none',
        callback: onRecaptchaSuccessed,
        'error-callback': onRecaptchaRejected,
      });

      return invisibleRecaptchaWidgetId;
    };

    const executeWithPromise = () => {
      if (typeof grecaptcha === 'undefined') {
        return Promise.reject(new Error('grecaptcha not found'));
      }

      invisibleRecaptchaPromise = createDeferredPromise();
      const widgetId = renderInvisibleRecaptcha();

      grecaptcha.execute(widgetId);

      return invisibleRecaptchaPromise.promise;
    };

    const cancelRecaptchaTimer = () => $timeout.cancel(recaptchaTimer);

    const checkRecaptchaIsNotReady = () =>
      !window.grecaptcha ||
      !window.grecaptcha?.render ||
      !window.grecaptcha?.reset;

    const waitRecaptchaLoaded = (callback) =>
      window.globalSDKObserver?.grecaptcha?.subscribe?.((sdk) => {
        callback(sdk);
      });

    const getResponse = (widgetId) => {
      if (
        typeof grecaptcha === 'undefined' ||
        widgetId === null ||
        widgetId === undefined
      ) {
        return null;
      }

      return grecaptcha.getResponse(widgetId);
    };

    const buildRecaptchaWrapper = (element) => {
      const wrapper = document.createElement('div');
      element.appendChild(wrapper);
      return wrapper;
    };

    const renderRecaptchaWidget = ({
      element,
      callback,
      clickCallback,
      errorCallback,
      expiredCallback,
      timeoutCallback,
      onRendered,
      isInvisible = true,
    }) => {
      let widgetId = null;

      const handleCallback = (token) => {
        cancelRecaptchaTimer();
        document.getElementById('g-recaptcha-response').value = token;
        callback?.(token);

        if (isInvisible) {
          // reset invisible reCAPTCHA widget after token is collected
          $timeout(() => {
            resetWithWidgetId(widgetId);
          }, 50);
        }
      };
      // triggered by incorrect site key or secret key, causing rendering failure
      // do not reset the reCAPTCHA widget, as it may cause an infinite loop.
      const handleErrorCallback = () => {
        cancelRecaptchaTimer();
        logger.error('failed to render recaptcha', {
          grecaptha: window.grecaptcha,
          // eslint-disable-next-line compat/compat
          network: window.navigator.connection?.effectiveType,
        });
        errorCallback?.();
      };
      // triggered by token expiration.
      const handleExpiredCallback = () => {
        if (isInvisible) {
          resetWithWidgetId(widgetId);
        }
        logger.error('the recaptcha token is expired', {
          grecaptha: window.grecaptcha,
          token: document.getElementById('g-recaptcha-response').value,
        });
        expiredCallback?.();
      };
      // manually check if the obtained token has expired.
      const handleClickCallback = () => {
        recaptchaTimer = $timeout(() => {
          logger.error('failed to get recaptcha token', {
            grecaptha: window.grecaptcha,
            // eslint-disable-next-line compat/compat
            network: window.navigator.connection?.effectiveType,
          });
          timeoutCallback?.();
        }, TIMEOUT);
        clickCallback?.();
      };

      // render recaptcha widget(invisible or checkbox)
      if (isInvisible) {
        widgetId = window.grecaptcha.render(buildRecaptchaWrapper(element), {
          badge: 'none',
          callback: handleCallback,
          'error-callback': handleErrorCallback,
          'expired-callback': handleExpiredCallback,
          sitekey: mainConfig.recaptchaSiteKey,
          size: 'invisible',
        });
      } else {
        widgetId = window.grecaptcha.render(buildRecaptchaWrapper(element), {
          callback: handleCallback,
          'error-callback': handleErrorCallback,
          'expired-callback': handleExpiredCallback,
          sitekey: mainConfig.recaptchaEnterpriseCheckboxSiteKey,
        });
      }

      onRendered?.(widgetId);

      $(element).on('click', handleClickCallback);
    };

    return {
      reset,
      execute,
      executeWithPromise,
      cancelRecaptchaTimer,
      checkRecaptchaIsNotReady,
      getResponse,
      resetWithWidgetId,
      renderRecaptchaWidget,
      waitRecaptchaLoaded,
    };
  },
]);
